import { useCallback, useEffect, useMemo, useState } from 'react';

// Web Admin → Event videos: YouTube links on events in the Results Hub,
// shown there as a Video button next to the event's results, and on the
// website's videos page and home page (backend/site_admin_routes.py,
// results_event_videos). A video is on the
// hill name and date the hub shows for the event, so the event is picked
// from the hub's own list. If that event's hill or date is later fixed in
// Results documents, the video stops showing until it's moved here.

const API_KEY = import.meta.env.VITE_API_KEY || '';

function fmtDate(iso) {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const eventKey = (hill, date) => `${date}|${hill}`;   // the date is always 10 characters

async function send(url, session, onExpired, options = {}) {
  const res = await fetch(url, {
    ...options, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.session_token}`, ...options.headers },
  }).catch(() => null);
  if (res && res.status === 401) { onExpired(); return null; }
  const data = res ? await res.json().catch(() => ({})) : {};
  return { ok: !!(res && res.ok), data };
}

// Add a video (no `video`), or change one's title or event.
function VideoForm({ video, seasons, session, onExpired, onDone, onCancel }) {
  const [year, setYear] = useState(() => (video ? Number(video.event_date.slice(0, 4)) : seasons[0]?.year));
  const [event, setEvent] = useState(video ? eventKey(video.hill, video.event_date) : '');
  const [title, setTitle] = useState(video ? video.title : '');
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const events = seasons.find(s => s.year === year)?.events || [];
  const eventFound = events.some(e => eventKey(e.hill, e.date) === event);

  const submit = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const body = { hill: event.slice(11), event_date: event.slice(0, 10), title };
    const r = video
      ? await send(`/api/site-admin/event-videos/${video.id}`, session, onExpired, { method: 'PUT', body: JSON.stringify(body) })
      : await send('/api/site-admin/event-videos', session, onExpired, { method: 'POST', body: JSON.stringify({ ...body, url }) });
    setSaving(false);
    if (!r) return;
    if (r.ok) onDone(); else setError(r.data.error || 'Could not save it. Please try again.');
  };

  return (
    <form className="site-admin-group site-admin-video-form" onSubmit={submit}>
      <div className="site-admin-doc-bar">
        <label className="site-field">
          <span>Season</span>
          <select value={year ?? ''} onChange={e => { setYear(Number(e.target.value)); setEvent(''); }}>
            {seasons.map(s => <option key={s.year} value={s.year}>{s.year}</option>)}
          </select>
        </label>
        <label className="site-field">
          <span>Event</span>
          <select value={eventFound ? event : ''} onChange={e => setEvent(e.target.value)}>
            <option value="">Choose the event…</option>
            {events.map(e => (
              <option key={eventKey(e.hill, e.date)} value={eventKey(e.hill, e.date)}>{fmtDate(e.date)}, {e.hill}</option>
            ))}
          </select>
        </label>
      </div>
      <label className="site-field">
        <span>Title <span className="site-admin-help">(shown when the pointer is over the Video button)</span></span>
        <input type="text" value={title} maxLength={120} placeholder="Chateau Impney 2019, as shown on TV" onChange={e => setTitle(e.target.value)} />
      </label>
      {!video && (
        <label className="site-field">
          <span>YouTube link <span className="site-admin-help">(on YouTube, click Share under the video, then Copy, and paste it here)</span></span>
          <input type="url" value={url} placeholder="https://youtu.be/…" onChange={e => setUrl(e.target.value)} />
        </label>
      )}
      {error && <p className="site-field-error" role="alert">{error}</p>}
      <div className="site-admin-doc-actions">
        <button type="submit" className="site-btn site-btn--ink" disabled={!eventFound || !title.trim() || (!video && !url.trim()) || saving}>
          {saving ? 'Saving…' : video ? 'Save' : 'Add video'}
        </button>
        <button type="button" className="site-link-button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function VideoRow({ video, found, seasons, session, onExpired, reload }) {
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');

  const remove = async () => {
    if (!window.confirm(`Take "${video.title}" off the Results Hub? The video stays on YouTube.`)) return;
    const r = await send(`/api/site-admin/event-videos/${video.id}`, session, onExpired, { method: 'DELETE' });
    if (!r) return;
    if (r.ok) reload(); else setError(r.data.error || 'Could not delete it.');
  };

  return (
    <li className="site-admin-page-doc">
      <div className="site-admin-page-doc-head">
        <div className="site-admin-doc-main">
          <strong>{video.title}</strong>
          <span className="site-admin-meta">
            {fmtDate(video.event_date)}, {video.hill} · <a href={video.url} target="_blank" rel="noreferrer">{video.url}</a>
          </span>
          {!found && (
            <span><span className="site-admin-flag">Not showing: the Results Hub has no event {video.hill} on this date any more. Use Change to choose its event again.</span></span>
          )}
        </div>
        <div className="site-admin-doc-actions">
          <button type="button" className="site-link-button" aria-expanded={editing} onClick={() => setEditing(v => !v)}>Change</button>
          <button type="button" className="site-link-button" onClick={remove}>Delete</button>
        </div>
      </div>
      {editing && (
        <VideoForm video={video} seasons={seasons} session={session} onExpired={onExpired}
          onDone={() => { setEditing(false); reload(); }} onCancel={() => setEditing(false)} />
      )}
      {error && <p className="site-field-error" role="alert">{error}</p>}
    </li>
  );
}

export default function EventVideos({ session, onExpired, Section }) {
  const [videos, setVideos] = useState(null);
  const [seasons, setSeasons] = useState(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    send('/api/site-admin/event-videos', session, onExpired).then(r => {
      if (!r) return;
      if (r.ok) setVideos(r.data.videos); else setError(r.data.error || 'Could not load the videos');
    });
  }, [session, onExpired]);

  useEffect(() => { load(); }, [load]);

  // The events, as the Results Hub lists them. Only dated ones can have a video.
  useEffect(() => {
    fetch('/api/results/index', { headers: { 'X-API-Key': API_KEY } })
      .then(res => (res.ok ? res.json() : Promise.reject(new Error('Could not load the Results Hub events'))))
      .then(data => setSeasons(data
        .map(s => ({ year: s.year, events: s.events.filter(e => e.date) }))
        .filter(s => s.events.length > 0)))
      .catch(e => setError(e.message));
  }, []);

  const hubEvents = useMemo(
    () => new Set((seasons || []).flatMap(s => s.events.map(e => eventKey(e.hill, e.date)))),
    [seasons],
  );

  const lost = videos && seasons ? videos.filter(v => !hubEvents.has(eventKey(v.hill, v.event_date))).length : 0;
  const summary = videos ? `${videos.length} ${videos.length === 1 ? 'video' : 'videos'}${lost ? `, ${lost} not showing` : ''}` : '';

  return (
    <Section id="event-videos" title="Event videos" summary={summary}>
      <p>
        Videos of events, shown in the <a href="/results-hub" target="_blank" rel="noreferrer">Results Hub</a> as
        a <strong>Video</strong> button next to the event&apos;s results, and on
        the <a href="/videos" target="_blank" rel="noreferrer">videos page</a> (the home page shows a different one
        each day). Put the video on YouTube first (it can be
        &ldquo;Unlisted&rdquo;), then add its link here.
      </p>
      {error && <p className="site-field-error">{error}</p>}
      {(!videos || !seasons) && !error && <p>Loading…</p>}
      {videos && seasons && (
        <>
          {videos.length > 0 && (
            <ul className="site-admin-page-doc-list">
              {videos.map(v => (
                <VideoRow key={v.id} video={v} found={hubEvents.has(eventKey(v.hill, v.event_date))} seasons={seasons}
                  session={session} onExpired={onExpired} reload={load} />
              ))}
            </ul>
          )}
          {adding ? (
            <VideoForm seasons={seasons} session={session} onExpired={onExpired}
              onDone={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
          ) : (
            <button type="button" className="site-btn site-btn--ghost-dark" onClick={() => setAdding(true)}>Add a video</button>
          )}
        </>
      )}
    </Section>
  );
}
